import React, { useEffect, useState, useId } from 'react';
import { Link } from 'react-router-dom';
import {
  GlobeAltIcon,
  UserGroupIcon,
  LightningBoltIcon,
  ChartBarIcon,
  OfficeBuildingIcon,
  ClipboardCheckIcon,
} from '@heroicons/react/outline';
import { SEOHead, seoConfig } from '../SEO';
import { businessMetrics } from '../../config/businessMetrics';

function HeatMapIcon({ className = '' }) {
  const gradientOuterId = `${useId()}-outer`;
  const gradientHaloId = `${useId()}-halo`;
  const gradientPinId = `${useId()}-pin`;
  const gradientCenterId = `${useId()}-center`;
  const shadowId = `${useId()}-shadow`;

  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      className={className}
      fill="none"
    >
      <defs>
        <linearGradient id={gradientOuterId} x1="12" y1="52" x2="54" y2="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#2563EB" />
          <stop offset="45%" stopColor="#6366F1" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
        <radialGradient id={gradientHaloId} cx="32" cy="32" r="26" gradientUnits="userSpaceOnUse">
          <stop offset="35%" stopColor="#93C5FD" stopOpacity="0" />
          <stop offset="100%" stopColor="#7C3AED" stopOpacity="0.35" />
        </radialGradient>
        <linearGradient id={gradientPinId} x1="22" y1="18" x2="42" y2="50" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#F3F4FF" />
          <stop offset="100%" stopColor="#C7D2FE" />
        </linearGradient>
        <linearGradient id={gradientCenterId} x1="27" y1="24" x2="37" y2="36" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38BDF8" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="150%" colorInterpolationFilters="sRGB">
          <feDropShadow dx="0" dy="12" stdDeviation="8" floodColor="#4f46e5" floodOpacity="0.25" />
        </filter>
      </defs>
      <g filter={`url(#${shadowId})`}>
        <circle cx="32" cy="32" r="24" fill={`url(#${gradientOuterId})`} />
        <circle cx="32" cy="32" r="24" fill={`url(#${gradientHaloId})`} />
        <path
          d="M32 17c-8.284 0-15 6.337-15 14.84 0 10.86 13.28 22.76 13.85 23.32.35.33.9.33 1.25 0C32.66 54.6 47 42.12 47 31.84 47 23.34 40.284 17 32 17zm0 24.72c-4.418 0-8-3.5-8-7.82s3.582-7.82 8-7.82 8 3.5 8 7.82-3.582 7.82-8 7.82z"
          fill={`url(#${gradientPinId})`}
          stroke="rgba(255, 255, 255, 0.45)"
          strokeWidth="1.2"
        />
        <circle
          cx="32"
          cy="33.9"
          r="5"
          fill={`url(#${gradientCenterId})`}
          stroke="rgba(255, 255, 255, 0.65)"
          strokeWidth="1.4"
        />
        <path
          d="M23 44.2c3.4-2.9 7-4.3 9-4.3s5.6 1.4 9 4.3"
          stroke="rgba(255, 255, 255, 0.55)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M24.8 23.5l6.2 3.9 9-6.4"
          stroke="rgba(255, 255, 255, 0.55)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="24.5" cy="23.7" r="2" fill="rgba(59, 130, 246, 0.65)" />
        <circle cx="39.5" cy="20.8" r="1.8" fill="rgba(129, 140, 248, 0.6)" />
        <circle cx="44" cy="27.5" r="2.3" fill="rgba(16, 185, 129, 0.55)" />
      </g>
    </svg>
  );
}

export default function BuyerSourcing() {
  const heroStats = [
    {
      value: businessMetrics.buyers.displayText,
      label: 'qualified relationships',
    },
    {
      value: businessMetrics.timeline.initialOffers.displayText,
      label: 'to first offers',
    },
    {
      value: businessMetrics.performance.valuationImprovement.displayText,
      label: 'valuation lift',
    },
  ];

  const highlightCards = [
    {
      title: 'Strategic radar',
      description: 'Corporate acquirers ranked by synergy layers and executive intent.',
      icon: GlobeAltIcon,
    },
    {
      title: 'Capital coverage',
      description: 'Private equity, family offices, and independent sponsors filtered by check size.',
      icon: OfficeBuildingIcon,
    },
    {
      title: 'Launch playbook',
      description: 'Sequenced outreach, tailored messaging, and warm introductions on day one.',
      icon: LightningBoltIcon,
    },
  ];

  const atlasSignals = [
    {
      label: 'Strategic fit',
      value: '18',
      tone: 'from-amiva-primary to-blue-500',
      detail: 'Competitors and adjacent platforms leaning into expansion conversations.',
      radius: 120,
      duration: 28,
      delay: 0,
      accent: 'rgba(79, 141, 255, 0.45)',
    },
    {
      label: 'Financial sponsors',
      value: '27',
      tone: 'from-amiva-accent to-purple-500',
      detail: 'Funds and family offices with active dry powder and aligned theses. Strong appetite for buy and build.',
      radius: 152,
      duration: 32,
      delay: 6,
      accent: 'rgba(128, 90, 213, 0.45)',
    },
    {
      label: 'Operators',
      value: '9',
      tone: 'from-emerald-500 to-teal-500',
      detail: 'Experienced operators and searchers ready to run diligence immediately.',
      radius: 188,
      duration: 36,
      delay: 10,
      accent: 'rgba(16, 185, 129, 0.4)',
    },
  ];

  const processSteps = [
    {
      label: '01',
      title: 'Position',
      copy: 'Frame the narrative, proof points, and non-negotiables.',
    },
    {
      label: '02',
      title: 'Map',
      copy: 'Surface every strategic and financial counterparty with live data.',
    },
    {
      label: '03',
      title: 'Prioritize',
      copy: 'Score by fit, capital, and timing so energy goes to momentum.',
    },
    {
      label: '04',
      title: 'Engage',
      copy: 'Coordinate outreach, intros, and competitive tension in one flow.',
    },
  ];

  const coverage = [
    {
      title: 'Strategic buyers',
      tagline: businessMetrics.buyerTypes.strategic.displayText,
      bullets: ['Corporate development teams', 'Platform add-ons and tuck-ins', 'Product + geographic adjacencies'],
      icon: ChartBarIcon,
    },
    {
      title: 'Financial buyers',
      tagline: businessMetrics.buyerTypes.financial.displayText,
      bullets: ['Private equity platforms', 'Family offices with mandates', 'Independent sponsors & capital partners'],
      icon: UserGroupIcon,
    },
  ];

  const deliverables = [
    {
      title: 'Interactive buyer map',
      copy: 'Live universe with signals, contacts, and priority tiers.',
    },
    {
      title: 'Executive brief',
      copy: 'Board-ready deck with narratives, clusters, and activation plan.',
    },
    {
      title: 'Outreach command center',
      copy: 'Real-time dashboard for NDAs, conversations, and heat.',
    },
  ];

  const [activeSignalIndex, setActiveSignalIndex] = useState(0);

  useEffect(() => {
    const rotation = setInterval(() => {
      setActiveSignalIndex((prev) => (prev + 1) % atlasSignals.length);
    }, 4200);

    return () => clearInterval(rotation);
  }, []);

  const activeSignal = atlasSignals[activeSignalIndex];

  return (
    <div className="bg-white">
      <SEOHead {...seoConfig.buyerSourcing} canonical="/services/buyer-sourcing" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-950 to-black py-20 sm:py-28 lg:py-36 text-white">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-40 -left-24 h-96 w-96 rounded-full bg-amiva-primary/50 blur-3xl" />
          <div className="absolute top-20 right-8 h-[30rem] w-[30rem] rounded-full bg-amiva-accent/30 blur-3xl" />
          <div className="absolute bottom-10 left-1/2 h-[35rem] w-[35rem] -translate-x-1/2 rounded-full bg-blue-500/20 blur-3xl" />
        </div>
        
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 sm:gap-14 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-blue-300 backdrop-blur-sm shadow-lg shadow-blue-500/20">
              <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse shadow-lg shadow-blue-400/50" />
              Buyer sourcing by Amiva
            </span>
            <h1 className="mt-8 text-3xl font-bold sm:text-4xl lg:text-6xl lg:leading-[1.1] text-white tracking-tight">
              Map every strategic and financial buyer <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-purple-400 bg-clip-text text-transparent">before you open</span> the data room
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-300 sm:text-lg">
              We combine intelligence, relationships, and narrative design so you know exactly who cares—and how to activate them.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:mt-10 sm:flex-row sm:items-center">
              <Link
                to="/valuation"
                className="group relative inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 px-8 py-4 text-base font-semibold text-white shadow-2xl shadow-blue-500/50 transition-all duration-300 hover:scale-105 hover:shadow-blue-500/60"
              >
                <span className="flex items-center gap-2">
                  See your buyer map
                  <svg className="h-5 w-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </Link>
              <a
                href="https://app.usemotion.com/meet/mike-pantouvakis/acquisition-discussion"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-2xl border-2 border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white/50 hover:bg-white/10"
              >
                Talk to an expert
              </a>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-12 sm:grid-cols-3 lg:mt-14">
              {heroStats.map((stat) => (
                <div key={stat.label} className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-6 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/15 hover:shadow-xl hover:shadow-blue-500/10">
                  <div className="text-5xl font-bold text-white">{stat.value}</div>
                  <div className="mt-2 text-xs font-medium uppercase tracking-wider text-gray-400">{stat.label}</div>
                  <div className="absolute top-0 right-0 h-20 w-20 translate-x-8 -translate-y-8 rounded-full bg-blue-500/20 blur-2xl transition-all group-hover:bg-blue-400/30" />
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -translate-x-4 -translate-y-4 rounded-[2.25rem] border border-white/5 bg-gradient-to-br from-white/5 to-white/10 blur-2xl sm:-translate-x-8 sm:-translate-y-6 sm:rounded-[3rem]" />
            <div className="relative overflow-hidden rounded-[2.25rem] border border-white/20 bg-gradient-to-br from-white/10 via-white/5 to-transparent p-8 sm:rounded-[3rem] sm:p-9 lg:p-10 backdrop-blur-xl shadow-2xl">
              <div
                className="mx-auto h-52 w-52 rounded-full border-2 border-blue-400/30 bg-gradient-to-br from-blue-500/30 via-purple-500/20 to-blue-600/30 p-6 shadow-2xl transition-all duration-500 sm:h-64 sm:w-64 sm:p-8"
                style={{ boxShadow: `0 0 60px ${activeSignal.accent}, 0 0 120px ${activeSignal.accent}40` }}
              >
                <div className="flex h-full flex-col items-center justify-center rounded-full border border-white/30 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm text-center shadow-inner">
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-300/80">Core profile</span>
                  <span className="mt-3 text-2xl font-bold text-white sm:text-3xl">You</span>
                  <span className="mt-3 text-xs font-medium text-gray-400">Signals, proof, vision</span>
                </div>
              </div>
              <div className="mt-6 grid gap-3 sm:mt-8">
                {atlasSignals.map((signal, index) => {
                  const isActive = index === activeSignalIndex;
                  return (
                    <button
                      key={signal.label}
                      type="button"
                      onMouseEnter={() => setActiveSignalIndex(index)}
                      onFocus={() => setActiveSignalIndex(index)}
                      className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all duration-300 cursor-pointer sm:px-5 sm:py-3.5 ${
                        isActive
                          ? 'border-blue-400/60 bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-xl shadow-blue-500/30 scale-105'
                          : 'border-white/15 bg-white/10 text-white/80 backdrop-blur-sm hover:border-white/30 hover:bg-white/15 hover:text-white hover:shadow-lg'
                      }`}
                    >
                      <span className="text-xs tracking-[0.15em] uppercase">{signal.label}</span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold transition-all ${isActive ? 'bg-white/20 text-white' : 'bg-white/20 text-white/90'}`}>
                        {signal.value}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="relative py-20 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-white">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 h-[400px] w-[400px] rounded-full bg-blue-400/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-purple-400/20 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="mb-16 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-blue-700 shadow-lg shadow-blue-500/10">
              <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              Our approach
            </span>
            <h2 className="mt-6 text-4xl font-bold text-gray-900 sm:text-5xl">
              Three pillars of <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">buyer intelligence</span>
            </h2>
          </div>

          <div className="grid gap-8 md:grid-cols-3 lg:gap-10">
            {highlightCards.map((card, idx) => {
              const colors = [
                { from: 'from-blue-600', to: 'to-blue-500', shadow: 'shadow-blue-500/40', hover: 'group-hover:shadow-blue-500/30', glow: 'from-blue-400/25 to-blue-500/20' },
                { from: 'from-purple-600', to: 'to-purple-500', shadow: 'shadow-purple-500/40', hover: 'group-hover:shadow-purple-500/30', glow: 'from-purple-400/25 to-purple-500/20' },
                { from: 'from-indigo-600', to: 'to-indigo-500', shadow: 'shadow-indigo-500/40', hover: 'group-hover:shadow-indigo-500/30', glow: 'from-indigo-400/25 to-indigo-500/20' },
              ];
              const color = colors[idx];
              
              return (
                <div 
                  key={card.title} 
                  className="group relative overflow-hidden rounded-3xl border-2 border-gray-200/70 bg-white p-8 sm:p-10 shadow-xl transition-all duration-500 hover:-translate-y-3 hover:border-blue-300/60 hover:shadow-2xl hover:shadow-blue-500/20"
                  style={{
                    animationDelay: `${idx * 100}ms`
                  }}
                >
                  {/* Animated background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-blue-500/5 to-purple-500/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  
                  {/* Top glow effect */}
                  <div className={`absolute -top-20 -right-20 h-48 w-48 rounded-full bg-gradient-to-br ${color.glow} blur-3xl opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:-top-16 group-hover:-right-16`} />
                  
                  <div className="relative">
                    {/* Icon container with enhanced styling */}
                    <div className="relative inline-flex">
                      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${color.from} ${color.to} blur-lg opacity-50 transition-all duration-500 group-hover:blur-xl group-hover:opacity-100`} />
                      <div className={`relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${color.from} ${color.to} text-white shadow-xl ${color.shadow} transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                        <card.icon className="h-8 w-8" />
                      </div>
                    </div>
                    
                    <h3 className="mt-8 text-2xl font-bold text-gray-900 transition-colors duration-300 sm:text-3xl">
                      {card.title}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
                      {card.description}
                    </p>
                    
                    {/* Decorative bottom accent */}
                    <div className="mt-8 flex items-center gap-2">
                      <div className={`h-1 w-12 rounded-full bg-gradient-to-r ${color.from} ${color.to} opacity-30 transition-all duration-500 group-hover:w-20 group-hover:opacity-70`} />
                      <div className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${color.from} ${color.to} opacity-50 transition-all duration-500 group-hover:opacity-100 group-hover:scale-150`} />
                    </div>
                  </div>

                  {/* Bottom corner accent */}
                  <div className={`absolute bottom-0 left-0 h-32 w-32 -translate-x-16 translate-y-16 rounded-full bg-gradient-to-tr ${color.glow} blur-2xl opacity-0 transition-all duration-700 group-hover:opacity-60 group-hover:-translate-x-12 group-hover:translate-y-12`} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Atlas */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 py-16 sm:py-20 lg:py-28">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-20 left-10 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />
          <div className="absolute bottom-20 right-10 h-96 w-96 rounded-full bg-purple-400/20 blur-3xl" />
        </div>
        
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 sm:gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-blue-600 shadow-lg shadow-blue-500/10">
              <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              The Amiva buyer atlas
            </span>
            <h2 className="mt-8 text-3xl font-bold text-gray-900 sm:text-5xl lg:text-5xl leading-tight">
              Every serious counterparty, <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">in one living view</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-gray-600 sm:text-lg">
              Filter by strategy, capital firepower, and timing. Watch signals update as conversations build momentum.
            </p>
            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-4 group">
                <span className="mt-1.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/30">
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-base text-gray-700 font-medium">Dynamic segmentation for sector, geography, and deal size.</span>
              </li>
              <li className="flex items-start gap-4 group">
                <span className="mt-1.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/30">
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-base text-gray-700 font-medium">Primary executives, warm introductions, and engagement history.</span>
              </li>
              <li className="flex items-start gap-4 group">
                <span className="mt-1.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/30">
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-base text-gray-700 font-medium">Heat signals that surface who is leaning in and why.</span>
              </li>
            </ul>
          </div>

          <div className="relative overflow-hidden rounded-[2.25rem] border-2 border-blue-200/50 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 p-7 sm:rounded-[2.5rem] sm:p-8 lg:p-10 shadow-2xl shadow-blue-500/10 backdrop-blur-sm">
            <div className="absolute -top-14 -left-16 h-52 w-52 rounded-full bg-gradient-to-br from-blue-400/25 to-blue-500/20 blur-3xl" />
            <div className="absolute -bottom-16 -right-16 h-60 w-60 rounded-full bg-gradient-to-br from-purple-400/25 to-purple-500/20 blur-3xl" />
            <div className="relative grid gap-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="relative flex h-14 w-14 items-center justify-center">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/25 to-purple-500/25 blur-lg" aria-hidden="true" />
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/90 p-2 shadow-xl shadow-blue-500/25 ring-2 ring-blue-200/40 backdrop-blur">
                      <HeatMapIcon className="h-full w-full" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Live Snapshot</p>
                    <h3 className="mt-1 text-3xl font-bold text-gray-900">Buyer heat map</h3>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-green-700 shadow-lg shadow-green-500/20">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Updated live
                </span>
              </div>

              <div className="relative mx-auto w-full max-w-xl sm:max-w-2xl">
                <div className="absolute -inset-x-8 -inset-y-4 rounded-[3rem] bg-gradient-to-br from-blue-500/10 via-white to-purple-500/10 blur-3xl" aria-hidden="true" />
                <div className="relative grid gap-3 sm:grid-cols-3 sm:gap-4">
                  {atlasSignals.map((signal, index) => {
                    const isActive = activeSignalIndex === index;
                    return (
                      <button
                        key={signal.label}
                        type="button"
                        onMouseEnter={() => setActiveSignalIndex(index)}
                        onFocus={() => setActiveSignalIndex(index)}
                        className={`group flex h-full flex-col gap-3 rounded-2xl border-2 p-4 lg:p-5 text-left transition-all duration-300 ${
                          isActive
                            ? 'border-blue-400/70 bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-xl shadow-blue-500/40'
                            : 'border-gray-200 bg-white text-gray-700 shadow-lg hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl'
                        }`}
                      >
                        <span
                          className={`inline-flex h-11 w-11 items-center justify-center rounded-xl text-base font-semibold transition-colors ${
                            isActive
                              ? 'bg-white/20 text-white shadow-inner shadow-blue-500/30'
                              : 'bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/30'
                          }`}
                        >
                          {signal.value}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-[0.16em] ${isActive ? 'text-white/90' : 'text-gray-500/90'}`}>
                          {signal.label}
                        </span>
                        <div
                          className={`mt-auto h-1 w-full rounded-full transition-all ${
                            isActive
                              ? 'bg-white/80 shadow-[0_0_12px_rgba(255,255,255,0.45)]'
                              : 'bg-gradient-to-r from-blue-200/60 via-transparent to-purple-200/60 group-hover:from-blue-300/70 group-hover:to-purple-300/70'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50/50 p-5 lg:p-6 shadow-xl shadow-blue-500/10">
                <div className="absolute top-0 right-0 h-24 w-24 translate-x-10 -translate-y-10 rounded-full bg-gradient-to-br from-blue-400/20 to-purple-400/20 blur-2xl" />
                <div className="relative">
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-600">Active Signal</div>
                  <div className="mt-3 flex items-center gap-4">
                    <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-lg font-bold text-white shadow-lg shadow-blue-500/30">
                      {activeSignal.value}
                    </span>
                    <span className="text-base font-bold uppercase tracking-wide text-gray-900">
                      {activeSignal.label}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{activeSignal.detail}</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
                <div className="group relative overflow-hidden rounded-2xl border-2 border-blue-200/50 bg-gradient-to-br from-white to-blue-50/50 p-4 text-center shadow-lg transition-all duration-300 hover:border-blue-300 hover:shadow-xl hover:-translate-y-1 sm:p-5">
                  <div className="absolute top-0 right-0 h-20 w-20 translate-x-8 -translate-y-8 rounded-full bg-blue-400/20 blur-2xl transition-all group-hover:scale-150" />
                  <p className="relative text-3xl font-bold text-gray-900">{businessMetrics.buyerTypes.total.interestedParties}</p>
                  <p className="relative text-xs font-semibold uppercase tracking-wider text-gray-500 mt-1">Avg parties</p>
                </div>
                <div className="group relative overflow-hidden rounded-2xl border-2 border-purple-200/50 bg-gradient-to-br from-white to-purple-50/50 p-4 text-center shadow-lg transition-all duration-300 hover:border-purple-300 hover:shadow-xl hover:-translate-y-1 sm:p-5">
                  <div className="absolute top-0 right-0 h-20 w-20 translate-x-8 -translate-y-8 rounded-full bg-purple-400/20 blur-2xl transition-all group-hover:scale-150" />
                  <p className="relative text-3xl font-bold text-gray-900">{businessMetrics.timeline.loiAverage.displayText}</p>
                  <p className="relative text-xs font-semibold uppercase tracking-wider text-gray-500 mt-1">To LOI</p>
                </div>
                <div className="group relative overflow-hidden rounded-2xl border-2 border-blue-200/50 bg-gradient-to-br from-white to-blue-50/50 p-4 text-center shadow-lg transition-all duration-300 hover:border-blue-300 hover:shadow-xl hover:-translate-y-1 sm:p-5">
                  <div className="absolute top-0 right-0 h-20 w-20 translate-x-8 -translate-y-8 rounded-full bg-blue-400/20 blur-2xl transition-all group-hover:scale-150" />
                  <p className="relative text-3xl font-bold text-gray-900">{businessMetrics.performance.higherValuations.displayText}</p>
                  <p className="relative text-xs font-semibold uppercase tracking-wider text-gray-500 mt-1">Above first offer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-10 left-10 h-[400px] w-[400px] rounded-full bg-blue-400/15 blur-3xl animate-pulse" style={{animationDuration: '8s'}} />
          <div className="absolute bottom-10 right-10 h-[400px] w-[400px] rounded-full bg-purple-400/15 blur-3xl animate-pulse" style={{animationDuration: '10s'}} />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Centered Header */}
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-blue-700">
              How it unfolds
            </span>
            <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-bold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              A four-beat rhythm that <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 bg-clip-text text-transparent">builds competitive tension</span>
            </h2>
            
            {/* Compact Inline Stats */}
            <div className="mx-auto mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600 sm:gap-6 sm:text-base">
              {heroStats.map((stat, index) => (
                <div key={stat.label} className="flex items-baseline gap-1.5">
                  <span className="text-xl font-bold text-gray-900 sm:text-2xl">{stat.value}</span>
                  <span className="text-gray-600">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Unified Process Flow */}
          <div className="mt-10 sm:mt-12">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6">
              {processSteps.map((step, index) => {
                const colors = [
                  { from: 'from-blue-600', to: 'to-blue-500', shadow: 'shadow-blue-500/40', border: 'border-blue-300/50', bg: 'from-blue-500/0 via-blue-500/5 to-blue-500/10', glow: 'from-blue-400/20 to-blue-500/20' },
                  { from: 'from-purple-600', to: 'to-purple-500', shadow: 'shadow-purple-500/40', border: 'border-purple-300/50', bg: 'from-purple-500/0 via-purple-500/5 to-purple-500/10', glow: 'from-purple-400/20 to-purple-500/20' },
                  { from: 'from-indigo-600', to: 'to-indigo-500', shadow: 'shadow-indigo-500/40', border: 'border-indigo-300/50', bg: 'from-indigo-500/0 via-indigo-500/5 to-indigo-500/10', glow: 'from-indigo-400/20 to-indigo-500/20' },
                  { from: 'from-blue-600', to: 'to-purple-600', shadow: 'shadow-purple-500/40', border: 'border-purple-300/50', bg: 'from-blue-500/0 via-purple-500/5 to-purple-500/10', glow: 'from-blue-400/20 to-purple-500/20' },
                ];
                const color = colors[index];
                
                return (
                  <div 
                    key={step.label} 
                    className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-blue-300/50 hover:shadow-xl sm:p-6"
                  >
                    {/* Animated background gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${color.bg} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                    
                    <div className="relative flex items-start gap-4">
                      {/* Step number badge */}
                      <div className="relative flex-shrink-0">
                        <div className={`absolute inset-0 rounded-lg bg-gradient-to-br ${color.from} ${color.to} blur-md opacity-40 transition-all duration-300 group-hover:blur-lg group-hover:opacity-70`} />
                        <div className={`relative flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br ${color.from} ${color.to} text-lg font-bold text-white shadow-lg transition-all duration-300 group-hover:scale-105`}>
                          {step.label}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-gray-600">
                          {step.copy}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-950 to-black py-20 sm:py-24 lg:py-32 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 h-[500px] w-[500px] rounded-full bg-blue-500/30 blur-3xl animate-pulse" style={{animationDuration: '10s'}} />
          <div className="absolute bottom-20 right-10 h-[500px] w-[500px] rounded-full bg-purple-500/30 blur-3xl animate-pulse" style={{animationDuration: '12s'}} />
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 sm:gap-14 lg:grid-cols-[1fr_1.5fr] lg:gap-16">
            {/* Left column - Header */}
            <div className="lg:sticky lg:top-8 lg:self-start">
              <span className="inline-flex items-center gap-2 rounded-full border-2 border-white/20 bg-white/5 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white/90 shadow-lg shadow-blue-500/10 backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                Who we bring to the table
              </span>
              <h2 className="mt-8 text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
                Coverage across <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-purple-400 bg-clip-text text-transparent">every strategic and financial</span> ecosystem
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-white/80 sm:text-xl">
                Expect curated conversations with {businessMetrics.buyerTypes.total.displayText}. The list evolves as signals change.
              </p>
              
              {/* Decorative element */}
              <div className="mt-8 flex items-center gap-3">
                <div className="h-1 w-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                <div className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                <div className="h-1 w-12 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 opacity-50" />
              </div>
            </div>

            {/* Right column - Coverage cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
              {coverage.map((bucket, index) => {
                const colors = [
                  { icon: 'from-blue-500 to-blue-600', glow: 'from-blue-500/30 to-blue-600/20', border: 'hover:border-blue-400/40' },
                  { icon: 'from-purple-500 to-purple-600', glow: 'from-purple-500/30 to-purple-600/20', border: 'hover:border-purple-400/40' },
                ];
                const color = colors[index];

                return (
                  <div 
                    key={bucket.title} 
                    className={`group relative overflow-hidden rounded-3xl border-2 border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/20 ${color.border} sm:p-8 lg:p-9`}
                    style={{
                      animationDelay: `${index * 150}ms`
                    }}
                  >
                    {/* Animated background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-blue-500/5 to-purple-500/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    
                    {/* Top glow effect */}
                    <div className={`absolute -top-24 -right-24 h-56 w-56 rounded-full bg-gradient-to-br ${color.glow} blur-3xl opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:-top-20 group-hover:-right-20`} />
                    
                    <div className="relative">
                      {/* Icon with glow effect */}
                      <div className="relative inline-flex">
                        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${color.icon} blur-lg opacity-60 transition-all duration-500 group-hover:blur-xl group-hover:opacity-100`} />
                        <div className={`relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${color.icon} text-white shadow-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                          <bucket.icon className="h-7 w-7" />
                        </div>
                      </div>
                      
                      <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.28em] text-white/50 transition-colors duration-300 group-hover:text-white/70">
                        {bucket.tagline}
                      </p>
                      <h3 className="mt-3 text-2xl font-bold text-white transition-colors duration-300 group-hover:text-blue-100 sm:text-3xl">
                        {bucket.title}
                      </h3>
                      
                      <ul className="mt-6 space-y-4">
                        {bucket.bullets.map((item, idx) => (
                          <li 
                            key={item} 
                            className="flex items-start gap-3.5 transition-all duration-300"
                            style={{
                              transitionDelay: `${idx * 50}ms`
                            }}
                          >
                            <span className="mt-1.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-400/30 to-purple-400/30 ring-2 ring-white/20 transition-all duration-300 group-hover:ring-white/40 group-hover:scale-110">
                              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-blue-400 to-purple-400" />
                            </span>
                            <span className="text-base text-white/70 transition-colors duration-300 group-hover:text-white/90">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* Bottom decorative line */}
                      <div className="mt-8 flex items-center gap-2">
                        <div className={`h-1 w-16 rounded-full bg-gradient-to-r ${color.icon} opacity-30 transition-all duration-500 group-hover:w-24 group-hover:opacity-70`} />
                        <div className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${color.icon} opacity-50 transition-all duration-500 group-hover:opacity-100 group-hover:scale-150`} />
                      </div>
                    </div>

                    {/* Bottom corner accent */}
                    <div className={`absolute bottom-0 left-0 h-32 w-32 -translate-x-16 translate-y-16 rounded-full bg-gradient-to-tr ${color.glow} blur-2xl opacity-0 transition-all duration-700 group-hover:opacity-80 group-hover:-translate-x-12 group-hover:translate-y-12`} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="relative py-20 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-br from-white via-purple-50/20 to-white">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/3 left-1/3 h-[450px] w-[450px] rounded-full bg-purple-400/20 blur-3xl" />
          <div className="absolute bottom-1/3 right-1/3 h-[450px] w-[450px] rounded-full bg-blue-400/20 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-purple-700 shadow-lg shadow-purple-500/10">
              <span className="h-2 w-2 rounded-full bg-purple-500 animate-pulse" />
              Tangible output
            </span>
            <h2 className="mt-8 text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl">
              You leave with assets you can <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">act on immediately</span>
            </h2>
            <p className="mt-6 text-lg text-gray-600 sm:text-xl">
              Everything is presentation-ready and refreshed as the market shifts.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 text-left sm:grid-cols-3 lg:gap-10">
            {deliverables.map((item, idx) => {
              const colors = [
                { icon: 'from-blue-600 to-blue-500', glow: 'from-blue-400/25 to-blue-500/20', shadow: 'shadow-blue-500/40' },
                { icon: 'from-purple-600 to-purple-500', glow: 'from-purple-400/25 to-purple-500/20', shadow: 'shadow-purple-500/40' },
                { icon: 'from-indigo-600 to-indigo-500', glow: 'from-indigo-400/25 to-indigo-500/20', shadow: 'shadow-indigo-500/40' },
              ];
              const color = colors[idx];

              return (
                <div 
                  key={item.title} 
                  className="group relative overflow-hidden rounded-3xl border-2 border-gray-200/70 bg-white p-7 shadow-xl transition-all duration-500 hover:-translate-y-3 hover:border-purple-300/60 hover:shadow-2xl hover:shadow-purple-500/20 sm:p-8"
                  style={{
                    animationDelay: `${idx * 100}ms`
                  }}
                >
                  {/* Animated background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 via-purple-500/5 to-blue-500/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  
                  {/* Top glow effect */}
                  <div className={`absolute -top-20 -right-20 h-48 w-48 rounded-full bg-gradient-to-br ${color.glow} blur-3xl opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:-top-16 group-hover:-right-16`} />
                  
                  <div className="relative">
                    {/* Icon with glow effect */}
                    <div className="relative inline-flex">
                      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${color.icon} blur-lg opacity-50 transition-all duration-500 group-hover:blur-xl group-hover:opacity-100`} />
                      <div className={`relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${color.icon} text-white shadow-xl ${color.shadow} transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                        <ClipboardCheckIcon className="h-7 w-7" />
                      </div>
                    </div>
                    
                    <h3 className="mt-6 text-xl font-bold text-gray-900 transition-colors duration-300 sm:text-2xl">
                      {item.title}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-gray-600">
                      {item.copy}
                    </p>
                    
                    {/* Decorative bottom accent */}
                    <div className="mt-6 flex items-center gap-2">
                      <div className={`h-1 w-12 rounded-full bg-gradient-to-r ${color.icon} opacity-30 transition-all duration-500 group-hover:w-20 group-hover:opacity-70`} />
                      <div className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${color.icon} opacity-50 transition-all duration-500 group-hover:opacity-100 group-hover:scale-150`} />
                    </div>
                  </div>

                  {/* Bottom corner accent */}
                  <div className={`absolute bottom-0 left-0 h-32 w-32 -translate-x-16 translate-y-16 rounded-full bg-gradient-to-tr ${color.glow} blur-2xl opacity-0 transition-all duration-700 group-hover:opacity-60 group-hover:-translate-x-12 group-hover:translate-y-12`} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-950 to-black py-20 sm:py-24 lg:py-32 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-blue-500/30 blur-3xl animate-pulse" style={{animationDuration: '8s'}} />
          <div className="absolute top-1/4 left-1/4 h-[400px] w-[400px] rounded-full bg-purple-500/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-indigo-500/20 blur-3xl" />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

        <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-10 px-4 text-center sm:px-6 lg:px-8">
          {/* Badge */}
          <span className="inline-flex items-center gap-2 rounded-full border-2 border-white/20 bg-white/5 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white/90 shadow-lg shadow-blue-500/10 backdrop-blur-sm">
            <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
            Ready to begin?
          </span>

          <h2 className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            <span className="text-white">
              See the buyer field from <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">every angle</span>
            </span>
          </h2>
          
          <p className="max-w-2xl text-lg leading-relaxed text-white sm:text-xl">
            In 30 minutes we can show you the strategic and financial profiles we would activate for your company.
          </p>

          <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:gap-5">
            <Link
              to="/valuation"
              className="group relative inline-flex items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 px-10 py-5 text-lg font-bold text-white shadow-2xl shadow-blue-500/50 transition-all duration-300 hover:scale-105 hover:shadow-blue-500/60"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <span className="relative flex items-center gap-2">
                Start your exit process
                <svg className="h-5 w-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </Link>

            <Link
              to="https://app.usemotion.com/meet/mike-pantouvakis/acquisition-discussion"
              className="group inline-flex items-center justify-center rounded-2xl border-2 border-white/30 bg-white/5 px-10 py-5 text-lg font-bold text-white backdrop-blur-sm transition-all duration-300 hover:border-white/50 hover:bg-white/10 hover:shadow-lg"
            >
              <span className="flex items-center gap-2">
                Book partner led call
                <svg className="h-5 w-5 transition-transform group-hover:translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">200k+</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-white/60">Qualified buyers</div>
            </div>
            <div className="h-12 w-px bg-white/20" />
            <div className="text-center">
              <div className="text-3xl font-bold text-white">3 weeks</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-white/60">To first offers</div>
            </div>
            <div className="h-12 w-px bg-white/20" />
            <div className="text-center">
              <div className="text-3xl font-bold text-white">35%</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-white/60">Valuation lift</div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
